import styles from '../styles/LoginForm.module.scss';
import logo from '../assets/logo.png';
import entraLogo from '../assets/microsoft-entra-icon.png';
import { useLocalization } from '@progress/kendo-react-intl';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLoginFormValidator } from '../components/login/customHooks/useLoginFormValidator';
import clsx from 'clsx';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { loadingPanel } from '../components/LoadingPanel';
import { Switch } from '@progress/kendo-react-inputs';
import { AppContext } from '../AppContext';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { locales } from '../resources/locales';
import { emailValidator } from '../components/login/validators';

type LoginFormState = {
  username: string;
  password: string;
};

type LoginPageProps = {
  handleLogin?: () => void;
};

export const LoginPage = ({ handleLogin = () => { } }: LoginPageProps) => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const isLocalLoginEnabled = (process.env.REACT_APP_LOCAL ?? 'true').toLowerCase() === 'true';
  const isSsoLoginEnabled = (process.env.REACT_APP_SSO ?? 'true').toLowerCase() === 'true';
  const showLoginModeSwitch = isLocalLoginEnabled && isSsoLoginEnabled;

  const localizationService = useLocalization();
  const navigate = useNavigate();

  const { localeId, onLanguageChange } = useContext(AppContext);
  const currentLanguage = locales.find(item => item.localeId === localeId);

  const [loading, setLoading] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [sentMessage, setSentMessage] = useState<string>('');
  const [imageLogo, setImageLogo] = useState<string | null>(null);

  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [errorEmail, setErrorEmail] = useState<boolean>(true);

  const [form, setForm] = useState<LoginFormState>({
    username: "",
    password: "",
  });

  const { errors, validateForm, onBlurField, getFieldError } = useLoginFormValidator(form);

  const [switchADLogin, setSwitchADLogin] = useState<boolean>(() => {
    if (isSsoLoginEnabled && !isLocalLoginEnabled) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (!showLoginModeSwitch) {
      setSwitchADLogin(isSsoLoginEnabled && !isLocalLoginEnabled);
    }
  }, [showLoginModeSwitch, isLocalLoginEnabled, isSsoLoginEnabled]);

  const showTimedError = useCallback((text: string, delay = 5000) => {
    setVisible(true);
    setMessage(text);
    setTimeout(() => {
      setVisible(false);
    }, delay);
  }, []);

  const onUpdateField = (e: any) => {
    const field = e.target.name as keyof typeof errors;
    const nextFormState = {
      ...form,
      [field]: e.target.value,
    };
    setForm(nextFormState);
    if (errors[field].dirty)
      validateForm({
        form: nextFormState,
        errors,
        field,
      });
  };

  const onSubmitForm = async (e: any) => {
    e.preventDefault();
    const { isValid } = validateForm({ form, errors, forceTouchErrors: true });
    if (!isValid || !SERVER_URL) {
      return;
    }

    setLoading(true);

    const params = {
      username: form.username,
      password: form.password,
    };

    try {
      const response = await axios.post(`${SERVER_URL}/account/login`, params);

      const userType = response?.data?.data?.userType;
      const admin = response?.data?.data?.admin;

      localStorage.setItem("accessToken", response?.data?.data?.token);
      localStorage.setItem("username", response?.data?.data?.username);
      localStorage.setItem("email", response?.data?.data?.email);
      localStorage.setItem("accountId", response?.data?.data?.id);
      localStorage.setItem("admin", admin);
      localStorage.setItem("userType", userType);
      if (userType == 'StandardOnlySyncro') {
        navigate('/home/synoptic')
      } else if (response?.data?.data?.username == 'azure') {
        navigate('/home/azure_map')
      } else {
        navigate('/home/dashboard')
      }
      setLoading(false)
    }
    catch (error: any) {
      let message = error?.response?.data?.errorMessage ?? error?.response?.statusText
      let code = error?.response?.status
      if (code == 500 && message!.includes('account not enabled')) {
        setVisible(true)
        const userUnauthorized = localizationService.toLanguageString('login_error.account_not_enabled', 'NOT FOUND')
        setMessage(userUnauthorized)
        setTimeout(() => {
          setVisible(false)
        }, 10000)
      }
      else if (code == 500 && message!.includes('User or password are invalid')) {
        setVisible(true)
        const userUnauthorized = localizationService.toLanguageString('login_error.invalid_credentials', 'NOT FOUND')
        setMessage(userUnauthorized)
        setTimeout(() => {
          setVisible(false)
        }, 10000)
      }
      else if (message!.includes(`User doesn't have an email`)) {
        const userWithNoEmail = localizationService.toLanguageString('login_error.user_with_no_email', 'NOT FOUND')
        setVisible(true)
        setMessage(userWithNoEmail)
        setTimeout(() => {
          setVisible(false)
        }, 10000)
      }
      setLoading(false)
    };
  };

  const handleToggleSwitch = () => {
    setSwitchADLogin(prev => !prev);
    if (showForgotPassword) {
      setShowForgotPassword(false);
    }
  };

  const getImageLogo = async () => {
    if (!SERVER_URL) {
      return;
    }

    axios.get(`${SERVER_URL}/account/logo`)
      .then((res) => {
        const image = `data:image/png;base64, ${res?.data?.data.image}`;
        setImageLogo(image);
      }).catch(() => {
        setImageLogo(null);
      });
  };

  useEffect(() => {
    getImageLogo();
  }, []);

  const onLanguageChangeSystem: any = useCallback((event: any) => {
    localStorage.setItem('languageId', event.value.localeId);
  }, []);

  const sendRecoveryEmail = (email: string) => {
    if (!SERVER_URL) {
      return;
    }

    axios.post(`${SERVER_URL}/account/password/forgot/${email}`)
      .then(() => {
        const recoverySentMessage = localizationService.toLanguageString('login_error.recovery_email_sent', 'NOT_FOUND');
        setSentMessage(recoverySentMessage);
        setShowForgotPassword(false);
        setUserEmail('');
        setTimeout(() => {
          setSentMessage('');
        }, 5000);
      }).catch((err) => {
        let code = err?.response?.status
        const recoveryError = err?.response?.data?.errorMessage ?? err?.response?.statusText;
        if (code == 500 && message!.includes('Account is not local')) {
          setVisible(true)
          const accountNotLocal = localizationService.toLanguageString('login_error.account_not_local', 'NOT FOUND')
          setMessage(accountNotLocal)
          setTimeout(() => {
            setVisible(false)
          }, 3000)
        }
        else if (recoveryError) {
          showTimedError(String(recoveryError), 3000);
        }
      });
  }

  const handleEmailChange = (event: any) => {

    const errorMessage: any = emailValidator(event.target.value, localizationService);

    setErrorEmail(Boolean(errorMessage));

    setUserEmail(event.target.value);

  };

  const isSubmitDisabled =
    errors.username.error ||
    errors.password.error ||
    !form.username ||
    !form.password;

  const sectionTitle = switchADLogin
    ? 'Microsoft Entra ID'
    : localizationService.toLanguageString('title', 'NOT_FOUND');

  const emailLabel = localizationService.toLanguageString('login.email', 'Email');
  const usernameLabel = localizationService.toLanguageString('login.username', 'Username');
  const passwordLabel = localizationService.toLanguageString('login.password', 'Password');

  return (
    <div className={styles.page}>
      <div className={styles.form}>
        {loading && loadingPanel}

        <header className={styles.header}>
          <img src={logo} className={styles.logo} alt="Smart Locator" />
          <h1 className={styles.textLocator}>{sectionTitle}</h1>
          {imageLogo ? <img src={imageLogo} className={styles.companyLogo} alt="Company logo" /> : null}
        </header>

        {showForgotPassword && !switchADLogin ? (
          <>
            <div className={styles.formGroup}>
              <p className={styles.forgotMessage}>
                {localizationService.toLanguageString('login.message', 'NOT_FOUND')}
              </p>
              <label className={styles.formLabel}>{emailLabel}</label>
              <input
                className={clsx(styles.formField, errorEmail && userEmail && styles.formFieldError)}
                type="text"
                aria-label="Email field"
                name="email"
                value={userEmail}
                onChange={handleEmailChange}
              />
            </div>

            <div className={styles.formActions}>
              <button
                className={styles.formSubmitBtn}
                disabled={errorEmail}
                onClick={() => sendRecoveryEmail(userEmail)}
              >
                {localizationService.toLanguageString('login.send_email', 'NOT_FOUND')}
              </button>
              <button className={styles.backToLogin} onClick={() => setShowForgotPassword(false)}>
                {localizationService.toLanguageString('login.back_to_login', 'NOT_FOUND')}
              </button>
            </div>
          </>
        ) : null}

        {!showForgotPassword && !switchADLogin ? (
          <form onSubmit={onSubmitForm} className={styles.staticForm}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{usernameLabel}</label>
              <input
                className={clsx(
                  styles.formField,
                  errors.username.dirty && errors.username.error && styles.formFieldError
                )}
                type="text"
                aria-label="Username field"
                name="username"
                value={form.username}
                onChange={onUpdateField}
                onBlur={onBlurField}
              />
              {errors.username.dirty && errors.username.error ? (
                <span className={styles.inputErrorOverlay}>{errors.username.message}</span>
              ) : null}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{passwordLabel}</label>
              <input
                className={clsx(
                  styles.formField,
                  errors.password.dirty &&
                  errors.password.error &&
                  styles.formFieldError
                )}
                type="password"
                aria-label="Password field"
                name="password"
                value={form.password}
                onChange={onUpdateField}
                onBlur={onBlurField}
              />
              {errors.password.dirty && errors.password.error ? (
                <span className={styles.inputErrorOverlay}>
                  {errors.password.message}
                </span>
              ) : null}
            </div>

            <div className={styles.formActions}>
              <button
                className={styles.formSubmitBtn}
                disabled={isSubmitDisabled}
                type="submit"
              >
                {localizationService.toLanguageString('login.log_in', 'NOT_FOUND')}
              </button>
              <button
                type="button"
                className={styles.textAction}
                onClick={() => setShowForgotPassword(true)}
              >
                {localizationService.toLanguageString('login.forgot_password', 'NOT_FOUND')}
              </button>
            </div>
          </form>
        ) : null}

        {switchADLogin ? (
          <div className={styles.entraActions}>
            <img src={entraLogo} width="48" height="48" alt="Microsoft Entra ID" />
            <p className={styles.entraTitle}>Microsoft Entra ID</p>
            <button type={'submit'} onClick={handleLogin} className={styles.signButton}>
              {localizationService.toLanguageString('login.log_in', 'NOT_FOUND')}
            </button>
          </div>
        ) : null}

        <footer className={styles.footerControls}>
          {showLoginModeSwitch && (
            <div className={styles.switchArea}>
              {!switchADLogin ?
                <p style={{ width: '170px', marginBottom: '0px' }}>{localizationService.toLanguageString('login.switch_to_sso', 'NOT FOUND')}:</p>
                :
                <p style={{ width: '160px', marginBottom: '0px' }}>{localizationService.toLanguageString('login.switch_to_static', 'NOT FOUND')}:</p>
              }

              <Switch
                className={'k-switch'}
                onChange={handleToggleSwitch}
                checked={switchADLogin}
              />
            </div>
          )}



          <div className={styles.languageArea}>
            <p className={styles.controlLabel}>{localizationService.toLanguageString('language', 'NOT_FOUND')}:</p>
            <DropDownList
              className={styles.languageDropdown}
              textField={'locale'}
              dataItemKey={'localeId'}
              data={locales}
              value={currentLanguage}
              onChange={(event: any) => {
                onLanguageChange(event);
                onLanguageChangeSystem(event);
              }}
            />
          </div>
        </footer >
      </div >

      <NotificationGroup
        style={{
          right: 0,
          bottom: 0
        }}
      >
        <Fade enter={true} exit={true}>
          {visible && (
            <Notification
              type={{ style: "error", icon: true }}
              closable={true}
              onClose={() => setVisible(false)}
            >
              <span>{message}</span>
            </Notification>
          )}
          {sentMessage !== '' && (
            <Notification
              type={{ style: "success", icon: true }}
              closable={true}
              onClose={() => setSentMessage('')}
            >
              <span>{sentMessage}</span>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>
    </div >
  );
};
